import React, { useEffect, useRef, useState } from 'react';
import { Input, Radio, Tooltip } from 'antd';
import { orderHomes } from 'utils/helpers';
import { SLH_TYPES } from 'api/constants';
import { COLORS } from 'utils/colors';
import {
  IconSoberLiving,
  IconChild,
  IconNoRelation,
  IconSmallArrow,
  IconSearch,
  IconDoer,
  IconGenderMismatch,
} from 'components';

const SlhSelect = ({
  data = [],
  value,
  onChange,
  onSearch,
  placeholder = 'SLH',
  placement = 'bottom',
  isWithAll = false,
  disabled = false,
  residentGender = null,
}) => {
  const [list, setList] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const ordered = orderHomes(Array.isArray(data) ? data : []);
    setList(ordered);
    setFiltered(ordered);
  }, [data]);

  useEffect(() => {
    if (value === null || value === undefined) {
      setIsOpen(false);
      setSearchValue('');
      setFiltered(list);
    }
  }, [list, value]);

  useEffect(() => {
    if (!isOpen) {
      setSearchValue('');
      setFiltered(list);
    }
  }, [isOpen, list]);

  const handleSearch = (e) => {
    const term = e.target.value || '';
    if (onSearch) onSearch(e);
    if (!term) {
      setFiltered(list);
      setSearchValue('');
      return;
    }
    setFiltered(list.filter((p) => p.name.toLowerCase().includes(term.toLowerCase())));
    setSearchValue(term);
  };

  const handleChange = (e) => {
    const id = e.target.value;
    const normalized = id === null || id === 'null' ? null : id;
    if (onChange) onChange(normalized);
    setIsOpen(false);
    setSearchValue('');
    setFiltered(list);
  };

  const hasGenderMismatch = (slh) => {
    if (!residentGender || !slh.gender) return false;
    const allowed = slh.gender.split(',').map((g) => g.trim());
    return !allowed.includes(residentGender);
  };

  const selectedLabel = () => {
    if (value === null) return 'All';
    if (value === undefined || value === '') return placeholder;
    const found = list.find((p) => String(p.id) === String(value));
    return found ? found.name : placeholder;
  };

  return (
    <div
      ref={dropdownRef}
      className={`relative z-50 ${disabled ? 'opacity-50 pointer-events-none' : ''}`}
    >
      <div
        className="flex items-center h-[40px] px-3 gap-x-2 text-base font-medium base-block rounded-lg border border-color-divider cursor-pointer"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="flex-1">{selectedLabel()}</span>
        <IconSmallArrow color={COLORS.black} className="rotate-[-90deg] w-3 h-3" />
      </div>

      <div
        className={`flex flex-col rounded-xl bg-white shadow-block-shadow p-4 gap-y-2 w-[450px] absolute left-0 ${placement === 'bottom' ? 'top-[calc(100%+16px)]' : 'bottom-[calc(100%+16px)]'} ${isOpen ? 'block' : 'hidden'}`}
      >
        <Input
          className="search text-lightBlue text-16 rounded-ten bg-light"
          type="text"
          placeholder="Search"
          addonBefore={<IconSearch color={COLORS.lightBlue} />}
          value={searchValue}
          onChange={handleSearch}
        />

        <div className="flex flex-col bg-light border border-divider rounded-lg max-h-[450px] overflow-y-auto">
          <Radio.Group
            onChange={handleChange}
            className="w-full flex flex-col gap-x-4"
            value={value === null ? null : +value}
          >
            {isWithAll && (
              <Radio
                className={`px-4 py-1.5 m-0 flex-1 flex items-center rounded-lg hover:bg-lightBlue ${value === null ? '!bg-white border border-orange' : ''}`}
                value={null}
              >
                <div className="flex items-center">
                  <p className="text-sm whitespace-nowrap">All</p>
                </div>
              </Radio>
            )}
            {filtered?.length > 0 &&
              Array.isArray(filtered) &&
              filtered.map((slh) => {
                const genderMismatch = hasGenderMismatch(slh);
                return (
                  <Radio
                    key={slh.id}
                    disabled={genderMismatch}
                    className={`${slh.type === SLH_TYPES.child ? 'ml-6' : ''} relative px-4 py-2 m-0 flex-1 flex items-center rounded-lg ${genderMismatch ? 'opacity-50' : 'hover:bg-lightBlue'} ${String(value) === String(slh.id) ? '!bg-white border border-orange' : ''}`}
                    value={slh.id}
                  >
                    <div className="flex items-center">
                      {slh.type === SLH_TYPES.parent ? (
                        <IconSoberLiving className="mr-2" />
                      ) : slh.type === SLH_TYPES.child ? (
                        <IconChild className="mr-2" />
                      ) : (
                        <IconNoRelation className="mr-2" />
                      )}
                      <p className="text-sm whitespace-nowrap">{slh.name}</p>
                      {slh.doer && <IconDoer className="absolute top-1 right-1 ml-2" />}
                      {genderMismatch && (
                        <Tooltip
                          title="Gender Mismatch"
                          placement="top"
                          mouseEnterDelay={0}
                          overlayInnerStyle={{
                            padding: '12px 16px',
                            borderRadius: '8px',
                            fontSize: '14px',
                            lineHeight: '16px',
                            fontFamily: 'poppins',
                            backgroundColor: '#000',
                          }}
                        >
                          <span className="absolute z-10 cursor-pointer top-1/2 left-4 -translate-y-1/2">
                            <IconGenderMismatch color={COLORS.red} className="w-4 h-4" />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </Radio>
                );
              })}
          </Radio.Group>
        </div>
      </div>
    </div>
  );
};

export default SlhSelect;
